import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, AlertTriangle, CheckCircle2, ArrowRight, ArrowLeft, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, inputCls, labelCls, trackEvent, money } from "../lib/market";
import { Eyebrow, GlassCard } from "../components/Section";

const STEPS = ["Describe", "Service", "Details", "Review"];
const DOCS = ["Contracts or agreements", "Letters / emails", "ID documents", "Court papers", "Receipts / invoices", "Photos / evidence"];

const Stepper = ({ step }) => (
  <div className="flex gap-2 mb-10" data-testid="wizard-stepper">
    {STEPS.map((s, i) => (
      <div key={s} className="flex-1">
        <div className={`h-1 rounded-full transition-colors ${i <= step ? "bg-turquoise" : "bg-white/10"}`} />
        <div className={`font-montserrat text-[10px] uppercase tracking-wider mt-2 ${i === step ? "text-turquoise" : "text-white/40"}`}>{i + 1}. {s}</div>
      </div>
    ))}
  </div>
);

const Suggestion = ({ ai }) => (
  <GlassCard className="mt-6 border-turquoise/25" data-testid="ai-suggestion-card">
    <Eyebrow>Justice-AI suggestion · {ai.confidence} confidence</Eyebrow>
    <h3 className="font-playfair text-2xl mt-2" data-testid="ai-suggested-service">{ai.service_name}</h3>
    <p className="font-inter text-sm text-white/60 mt-2">{ai.rationale}</p>
    {ai.safeguarding_flag && (
      <div className="mt-4 flex gap-2 text-red-300 font-inter text-sm"><ShieldAlert className="w-4 h-4 mt-0.5" /> Your situation may involve a safety risk. If you are in danger, contact emergency services. You can flag this for priority review in the next steps.</div>
    )}
    {ai.missing_information?.length > 0 && (
      <div className="mt-4 font-inter text-xs text-gold-200">Helpful to add: {ai.missing_information.join(" · ")}</div>
    )}
    <p className="font-inter text-[11px] text-white/35 mt-4">{ai.disclaimer}</p>
  </GlassCard>
);

const Field = ({ label, children }) => <label className="block"><span className={labelCls}>{label}</span>{children}</label>;

export default function RequestService() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [jur, setJur] = useState([]);
  const [services, setServices] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [f, setF] = useState({
    client_type: "individual", jurisdiction_id: "NG", description: "", service_id: params.get("service") || "",
    desired_outcome: "", key_dates: "", documents_available: [], preferred_channel: "in_app", budget: "",
    urgency: "medium", safeguarding_concern: false, safeguarding_note: "", funding_route: "paid", programme_id: "", intake_answers: {},
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    http.get("/services/jurisdictions").then(({ data }) => setJur(data.jurisdictions));
    http.get("/services", { params: { jurisdiction_id: "NG" } }).then(({ data }) => setServices(data.services));
    http.get("/justice-wallet/programmes").then(({ data }) => setProgrammes(data.programmes));
    trackEvent("service_request_started");
  }, []);

  const svc = useMemo(() => services.find((s) => s.service_id === f.service_id), [services, f.service_id]);
  const eligibleProgs = programmes.filter((p) => svc && p.approved_category_ids.includes(svc.category_id));

  const classify = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/service-requests/classify", { description: f.description, client_type: f.client_type, jurisdiction_id: f.jurisdiction_id });
      setAi(data);
      if (!params.get("service")) set("service_id", data.service_id);
      if (data.safeguarding_flag) set("safeguarding_concern", true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...f, budget: f.budget ? Number(f.budget) : null, programme_id: f.funding_route === "justice_wallet" ? f.programme_id : null, ai_suggested_service_id: ai?.service_id || null };
      const { data } = await http.post("/service-requests", body);
      setDone(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const canNext = [f.description.length >= 10, !!f.service_id, f.funding_route === "paid" || !!f.programme_id, true][step];

  if (done) return (
    <main className="bg-ink text-white min-h-screen py-20" data-testid="request-success">
      <div className="container mx-auto px-5 max-w-2xl text-center">
        <CheckCircle2 className="w-14 h-14 text-turquoise mx-auto mb-6" />
        <h1 className="font-playfair text-4xl">Request submitted.</h1>
        <p className="font-inter text-white/60 mt-4">We notified <b className="text-turquoise" data-testid="request-matched-count">{done.matched_count}</b> eligible verified lawyer(s). You'll be notified when they respond with terms.</p>
        {done.allocation && <p className="font-inter text-sm text-gold-200 mt-3" data-testid="request-allocation-status">Justice Wallet: {done.allocation.status.replace(/_/g, " ")}</p>}
        <Link to="/client/hub" className="btn-aj-primary mt-8" data-testid="request-go-hub">Track my matters <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </main>
  );

  return (
    <main className="bg-ink text-white min-h-screen py-12" data-testid="request-service-page">
      <div className="container mx-auto px-5 sm:px-8 max-w-3xl">
        <Eyebrow>Guided Request</Eyebrow>
        <h1 className="font-playfair text-4xl sm:text-5xl mt-3 mb-10">Tell us what you need.</h1>
        <Stepper step={step} />

        {step === 0 && (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="I am"><select data-testid="wizard-client-type" className={inputCls} value={f.client_type} onChange={(e) => set("client_type", e.target.value)}>
                <option value="individual">An individual</option><option value="business">A business</option><option value="ngo">An NGO</option><option value="institution">An institution</option></select></Field>
              <Field label="Country / jurisdiction"><select data-testid="wizard-jurisdiction" className={inputCls} value={f.jurisdiction_id} onChange={(e) => set("jurisdiction_id", e.target.value)}>
                {jur.map((j) => <option key={j.jurisdiction_id} value={j.jurisdiction_id} disabled={!j.active}>{j.name}{j.active ? "" : " — coming soon"}</option>)}</select></Field>
            </div>
            <Field label="Describe your problem or the outcome you want, in your own words">
              <textarea data-testid="wizard-description" rows={6} className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)}
                placeholder="e.g. My landlord wants to evict me without notice and keep my deposit…" />
            </Field>
            <p className="font-inter text-xs text-white/40">Avoid sharing names or highly sensitive details at this stage. Only your matched lawyer sees your description.</p>
            <button onClick={classify} disabled={busy || f.description.length < 10} data-testid="wizard-classify-btn" className="btn-aj-secondary">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Suggest a service with Justice-AI
            </button>
            {ai && <Suggestion ai={ai} />}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <Field label="Service (you can correct the suggestion)">
              <select data-testid="wizard-service-select" className={inputCls} value={f.service_id} onChange={(e) => set("service_id", e.target.value)}>
                <option value="">Select a service…</option>
                {services.map((s) => <option key={s.service_id} value={s.service_id}>{s.service_name}</option>)}
              </select>
            </Field>
            {ai && f.service_id !== ai.service_id && <p className="font-inter text-xs text-gold-200" data-testid="wizard-corrected-note">You changed the AI suggestion — thanks, that helps us route correctly.</p>}
            {svc && <GlassCard><p className="font-inter text-sm text-white/70">{svc.description}</p>
              <p className="font-inter text-xs text-white/45 mt-3">Turnaround {svc.estimated_turnaround} · {svc.fixed_fee_minor ? `Fixed fee ${money(svc.fixed_fee_minor)}` : "Lawyers will quote"}</p></GlassCard>}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            {(svc?.intake_schema || []).map((q) => (
              <Field key={q.key} label={q.label}><input data-testid={`wizard-intake-${q.key}`} className={inputCls} value={f.intake_answers[q.key] || ""}
                onChange={(e) => set("intake_answers", { ...f.intake_answers, [q.key]: e.target.value })} /></Field>
            ))}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Important dates or deadlines"><input data-testid="wizard-key-dates" className={inputCls} value={f.key_dates} onChange={(e) => set("key_dates", e.target.value)} placeholder="e.g. Court date 12 July" /></Field>
              <Field label="Urgency"><select data-testid="wizard-urgency" className={inputCls} value={f.urgency} onChange={(e) => set("urgency", e.target.value)}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></Field>
              <Field label="Preferred contact channel"><select data-testid="wizard-channel" className={inputCls} value={f.preferred_channel} onChange={(e) => set("preferred_channel", e.target.value)}>
                <option value="in_app">In-app</option><option value="email">Email</option><option value="phone">Phone</option><option value="whatsapp">WhatsApp</option></select></Field>
              <Field label="Budget (₦, optional)"><input data-testid="wizard-budget" type="number" min="0" className={inputCls} value={f.budget} onChange={(e) => set("budget", e.target.value)} /></Field>
            </div>
            <div><span className={labelCls}>Documents I have</span><div className="flex flex-wrap gap-2">
              {DOCS.map((d) => { const on = f.documents_available.includes(d); return (
                <button key={d} type="button" data-testid={`wizard-doc-${d.split(" ")[0].toLowerCase()}`} onClick={() => set("documents_available", on ? f.documents_available.filter((x) => x !== d) : [...f.documents_available, d])}
                  className={`px-3.5 py-1.5 rounded-full border font-inter text-xs transition-colors ${on ? "bg-turquoise/15 border-turquoise/50 text-turquoise" : "border-white/15 text-white/60 hover:border-white/40"}`}>{d}</button>); })}
            </div></div>
            <label className="flex items-start gap-3 font-inter text-sm text-white/75"><input type="checkbox" data-testid="wizard-safeguarding" checked={f.safeguarding_concern} onChange={(e) => set("safeguarding_concern", e.target.checked)} className="mt-1 accent-[#00C8D7]" />
              Someone's safety is at risk (violence, detention, a child at risk). Flag for priority safeguarding review.</label>
            {f.safeguarding_concern && <input data-testid="wizard-safeguarding-note" className={inputCls} placeholder="Optional short note for our safeguarding team" value={f.safeguarding_note} onChange={(e) => set("safeguarding_note", e.target.value)} />}
            <div><span className={labelCls}>How will this be paid?</span><div className="grid sm:grid-cols-2 gap-3">
              {[["paid", "I'll pay the agreed fee", "Secure checkout after you accept a lawyer's terms."], ["justice_wallet", "Request Justice Wallet sponsorship", "For eligible beneficiaries, subject to programme rules and approval."]].map(([v, t, d]) => (
                <button key={v} type="button" disabled={v === "justice_wallet" && !svc?.wallet_eligibility} data-testid={`wizard-funding-${v}`} onClick={() => set("funding_route", v)}
                  className={`text-left p-4 rounded-xl border transition-colors disabled:opacity-35 ${f.funding_route === v ? "border-turquoise/60 bg-turquoise/10" : "border-white/10 hover:border-white/30"}`}>
                  <div className="font-inter text-sm text-white">{t}</div><div className="font-inter text-xs text-white/45 mt-1">{d}</div></button>))}
            </div></div>
            {f.funding_route === "justice_wallet" && (
              <Field label="Funding programme"><select data-testid="wizard-programme" className={inputCls} value={f.programme_id} onChange={(e) => set("programme_id", e.target.value)}>
                <option value="">Select a programme…</option>
                {eligibleProgs.map((p) => <option key={p.programme_id} value={p.programme_id}>{p.name} — up to {money(p.max_per_case_minor)}</option>)}</select></Field>
            )}
          </div>
        )}

        {step === 3 && (
          <GlassCard data-testid="wizard-review">
            <Eyebrow className="mb-4">Review</Eyebrow>
            <dl className="grid sm:grid-cols-2 gap-4 font-inter text-sm">
              {[["Service", svc?.service_name], ["Client type", f.client_type], ["Jurisdiction", f.jurisdiction_id], ["Urgency", f.urgency],
                ["Funding", f.funding_route === "paid" ? "Paid engagement" : "Justice Wallet request"], ["Safeguarding", f.safeguarding_concern ? "Flagged" : "No"]].map(([k, v]) => (
                <div key={k}><dt className="text-white/40 text-xs">{k}</dt><dd className="text-white mt-0.5">{v}</dd></div>))}
            </dl>
            <p className="font-inter text-sm text-white/65 mt-5 border-t border-white/8 pt-5">{f.description}</p>
            <div className="flex gap-2 mt-5 text-xs font-inter text-white/45"><AlertTriangle className="w-4 h-4 text-gold-200 shrink-0" />
              Matched lawyers must complete a conflict-of-interest check before offering terms. No fees are charged until you accept terms.</div>
          </GlassCard>
        )}

        <div className="flex justify-between mt-10">
          <button onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="wizard-back-btn" className="btn-aj-ghost disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Back</button>
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext} data-testid="wizard-next-btn" className="btn-aj-primary disabled:opacity-40">Continue <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={submit} disabled={busy} data-testid="wizard-submit-btn" className="btn-aj-primary">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit request</button>
          )}
        </div>
      </div>
    </main>
  );
}
